import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2, Loader2 } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API_SUFFIX = process.env.REACT_APP_API_SUFFIX || '';

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact${API_SUFFIX}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error('Erreur serveur');
      
      toast({
        title: "Message envoyé !",
        description: "Nous vous répondrons dans les plus brefs délais. Un email de confirmation vous a été envoyé.",
      });

      setIsSuccess(true);

      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Contact - PULVISTAR | Nettoyage par drone en France</title>
        <meta name="description" content="Contactez PULVISTAR pour vos projets de nettoyage par drone. Disponible du lundi au samedi. Tél : 01 84 18 03 42. Email : contact@pulvistar.fr." />
        <link rel="canonical" href="https://pulvistar.fr/contact" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 bg-gradient-to-br from-blue-900 to-blue-800 text-white overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-400/20 rounded-full blur-3xl"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-6 bg-cyan-500/20 text-cyan-200 hover:bg-cyan-500/30 border-0 text-base px-4 py-2">
              <Phone className="w-4 h-4 mr-2" />
              Nous sommes à votre écoute
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight" style={{ fontFamily: '"Outfit", sans-serif', letterSpacing: '-0.5px' }}>
              Contactez-nous
            </h1>
            <p className="text-xl text-blue-100 leading-relaxed" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
              Une question ? Un projet ? Notre équipe vous répond rapidement
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: '"Outfit", sans-serif' }}>
                  Nos coordonnées
                </h2>
              </div>

              <Card className="p-6 border-gray-200">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-blue-900" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2" style={{ fontFamily: '"Outfit", sans-serif' }}>
                      Téléphone
                    </h3>
                    <a href="tel:+33184180342" className="text-gray-600 hover:text-blue-900 transition-colors" style={{ fontFamily: '"Inter", sans-serif' }}>
                      01 84 18 03 42
                    </a>
                    <p className="text-sm text-gray-500 mt-1" style={{ fontFamily: '"Inter", sans-serif' }}>
                      Du lundi au samedi
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 border-gray-200">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-blue-900" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2" style={{ fontFamily: '"Outfit", sans-serif' }}>
                      Email
                    </h3>
                    <a href="mailto:contact@pulvistar.fr" className="text-gray-600 hover:text-blue-900 transition-colors break-all" style={{ fontFamily: '"Inter", sans-serif' }}>
                      contact@pulvistar.fr
                    </a>
                    <p className="text-sm text-gray-500 mt-1" style={{ fontFamily: '"Inter", sans-serif' }}>
                      Réponse sous 24h
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 border-gray-200">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-blue-900" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2" style={{ fontFamily: '"Outfit", sans-serif' }}>
                      Zone d'intervention
                    </h3>
                    <p className="text-gray-600" style={{ fontFamily: '"Inter", sans-serif' }}>
                      France entière
                    </p>
                    <p className="text-sm text-gray-500 mt-1" style={{ fontFamily: '"Inter", sans-serif' }}>
                      Déplacements possibles
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 border-gray-200">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-blue-900" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2" style={{ fontFamily: '"Outfit", sans-serif' }}>
                      Horaires
                    </h3>
                    <p className="text-gray-600 text-sm" style={{ fontFamily: '"Inter", sans-serif' }}>
                      Lundi - Vendredi: 9h - 20h
                    </p>
                    <p className="text-gray-600 text-sm" style={{ fontFamily: '"Inter", sans-serif' }}>
                      Samedi: 9h - 18h
                    </p>
                    <p className="text-gray-600 text-sm" style={{ fontFamily: '"Inter", sans-serif' }}>
                      Dimanche: Fermé
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              {isSuccess ? (
                <Card className="p-12 lg:p-16 border-gray-200 shadow-xl text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-10 h-10 text-green-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: '"Outfit", sans-serif' }}>
                    Votre message a bien été envoyé !
                  </h2>
                  <p className="text-lg text-gray-600 mb-8" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
                    Notre équipe vous répondra dans les plus brefs délais. Un email de confirmation vous a été envoyé.
                  </p>
                  <Button size="lg" className="gap-2 bg-blue-900 hover:bg-blue-800" onClick={() => setIsSuccess(false)}>
                    Envoyer un autre message
                  </Button>
                </Card>
              ) : (
              <Card className="p-8 lg:p-12 border-gray-200 shadow-xl">
                <h2 className="text-2xl font-bold text-gray-900 mb-8" style={{ fontFamily: '"Outfit", sans-serif' }}>
                  Envoyez-nous un message
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <Label htmlFor="firstName" className="text-base font-semibold" style={{ fontFamily: '"Outfit", sans-serif' }}>
                        Prénom *
                      </Label>
                      <Input
                        id="firstName"
                        type="text"
                        placeholder="Votre prénom"
                        value={formData.firstName}
                        onChange={(e) => handleChange('firstName', e.target.value)}
                        className="h-12"
                        required
                      />
                    </div>
                    <div className="space-y-3">
                      <Label htmlFor="lastName" className="text-base font-semibold" style={{ fontFamily: '"Outfit", sans-serif' }}>
                        Nom *
                      </Label>
                      <Input
                        id="lastName"
                        type="text"
                        placeholder="Votre nom"
                        value={formData.lastName}
                        onChange={(e) => handleChange('lastName', e.target.value)}
                        className="h-12"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <Label htmlFor="email" className="text-base font-semibold" style={{ fontFamily: '"Outfit", sans-serif' }}>
                        Email *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="votre.email@exemple.fr"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        className="h-12"
                        required
                      />
                    </div>
                    <div className="space-y-3">
                      <Label htmlFor="phone" className="text-base font-semibold" style={{ fontFamily: '"Outfit", sans-serif' }}>
                        Téléphone
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="06 12 34 56 78"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        className="h-12"
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="subject" className="text-base font-semibold" style={{ fontFamily: '"Outfit", sans-serif' }}>
                      Sujet *
                    </Label>
                    <Input
                      id="subject"
                      type="text"
                      placeholder="Objet de votre message"
                      value={formData.subject}
                      onChange={(e) => handleChange('subject', e.target.value)}
                      className="h-12"
                      required
                    />
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="message" className="text-base font-semibold" style={{ fontFamily: '"Outfit", sans-serif' }}>
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      placeholder="Décrivez votre demande ou posez votre question..."
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      className="min-h-40"
                      required
                    />
                  </div>

                  <Button type="submit" size="lg" className="w-full gap-2 bg-blue-900 hover:bg-blue-800" disabled={isSubmitting} data-testid="contact-submit-btn">
                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                    {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                  </Button>
                </form>
              </Card>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map Placeholder Section */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: '"Outfit", sans-serif', letterSpacing: '-0.5px' }}>
              Intervention sur toute la France
            </h2>
            <p className="text-lg text-gray-600 mb-12" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
              De Paris à Marseille, de Bordeaux à Strasbourg, nous intervenons partout en France métropolitaine
            </p>
            
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { region: 'Île-de-France', city: 'Paris' },
                { region: 'Auvergne-Rhône-Alpes', city: 'Lyon' },
                { region: 'Provence-Côte d\'Azur', city: 'Marseille' },
                { region: 'Nouvelle-Aquitaine', city: 'Bordeaux' },
                { region: 'Occitanie', city: 'Toulouse' },
                { region: 'Pays de la Loire', city: 'Nantes' },
                { region: 'Hauts-de-France', city: 'Lille' },
                { region: 'Grand Est', city: 'Strasbourg' }
              ].map((location, index) => (
                <Card key={index} className="p-4 border-gray-200 hover:shadow-lg transition-shadow">
                  <MapPin className="w-6 h-6 text-cyan-500 mx-auto mb-2" />
                  <h3 className="font-semibold text-gray-900" style={{ fontFamily: '"Outfit", sans-serif' }}>
                    {location.city}
                  </h3>
                  <p className="text-sm text-gray-600" style={{ fontFamily: '"Inter", sans-serif' }}>
                    {location.region}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <Card className="max-w-4xl mx-auto p-12 text-center bg-gradient-to-br from-blue-900 to-blue-800 text-white border-0">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6" style={{ fontFamily: '"Outfit", sans-serif', letterSpacing: '-0.5px' }}>
              Besoin d'un devis ?
            </h2>
            <p className="text-xl text-blue-100 mb-8" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
              Obtenez une estimation détaillée pour votre projet en quelques minutes
            </p>
            <Button size="lg" className="gap-2 bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = '/devis'}>
              <CheckCircle2 className="w-5 h-5" />
              Demander un devis gratuit
            </Button>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Contact;
